import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AdminContext = createContext();

export const useAdmin = () => useContext(AdminContext);

// Initial Fallback Data
const initialData = {
  home: {
    heroTitle: 'Where Trust Meets Transparency',
    heroDesc: 'SarwaGroup is a diversified business group working across Education, Medical and Construction Material verticals.',
    tagline: 'Where Trust Meets Transparency',
    yearEstablished: '2021',
    heroImage: '',
  },
  about: {
    title: 'Building Trust. Creating Value.',
    description: 'SarwaGroup एक diversified business group है जो Education, Medical और Construction Material जैसे विभिन्न business verticals में कार्य करता है।',
    vision: 'To become a leading diversified business group known for quality and innovation.',
    mission: 'To deliver exceptional value through our diverse business verticals.',
    aboutImage: '',
    ownerName: 'Mr. Rajesh Mehta',
  },
  services: [],
  products: [],
  gallery: [],
  portfolio: [],
  team: [],
  testimonials: [],
  careers: [],
  whyChoose: [],
  whyJoin: [],
  offices: [],
  contact: {
    email: 'connectsarwagroup@gmail.com',
    phone: '+91 0000000000',
    address: '15323 Business Road, Raipur, CG',
    hours: '10am - 6pm',
    workingDays: 'Mon - Sat',
  },
  partners: [],
  inquiries: [],
  journey: [],
};

export const AdminProvider = ({ children }) => {
  const [data, setData] = useState(initialData);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('access_token');
  });
  const [loading, setLoading] = useState(false);

  // ========================================================
  // 🔄 MAPPING HELPERS (Backend <-> Frontend Field Converters)
  // ========================================================

  const mapHomeFromBE = (be) => ({
    heroTitle: be?.hero_title || initialData.home.heroTitle,
    heroDesc: be?.hero_description || initialData.home.heroDesc,
    tagline: be?.tagline || initialData.home.tagline,
    yearEstablished: be?.year_established ? String(be.year_established) : initialData.home.yearEstablished,
    heroImage: be?.hero_image || null,
  });

  const mapAboutFromBE = (be) => ({
    title: be?.page_title || initialData.about.title,
    description: be?.description || initialData.about.description,
    vision: be?.vision || initialData.about.vision,
    mission: be?.mission || initialData.about.mission,
    aboutImage: be?.about_image || null,
    ownerName: be?.owner_name || initialData.about.ownerName,
  });

  const mapContactFromBE = (be) => ({
    email: be?.email || initialData.contact.email,
    phone: be?.phone || initialData.contact.phone,
    address: be?.office_address || initialData.contact.address,
    hours: be?.working_hours || initialData.contact.hours,
    workingDays: be?.working_days || initialData.contact.workingDays,
  });

  const mapContactToBE = (fe) => ({
    email: fe.email,
    phone: fe.phone,
    office_address: fe.address,
    working_hours: fe.hours,
    working_days: fe.workingDays,
  });

  const mapItemToBE = (section, item) => {
    switch (section) {
      case 'services':
        return {
          title: item.title,
          icon: item.icon || '🎓',
          description: item.description,
          color: item.color || '#c9a227',
        };
      case 'products':
        return {
          name: item.name,
          category: (item.category || 'other').toLowerCase(),
          price: parseFloat(String(item.price).replace(/[^0-9.]/g, '')) || 0,
          icon: item.icon || '📦',
          description: item.description || '',
        };
      case 'testimonials':
        return {
          client_name: item.name || item.client_name,
          role_company: item.role || item.role_company || 'Client',
          rating: parseInt(item.rating) || 5,
          testimonial_text: item.text || item.testimonial_text,
        };
      case 'careers':
        return {
          job_title: item.title || item.job_title,
          department: (item.department || 'technology').toLowerCase(),
          location: item.location || 'Raipur',
          job_type: (item.type || item.job_type || 'full_time').toLowerCase().replace(' ', '_'),
          experience_required: item.experience || item.experience_required || '0-2 years',
          job_description: item.description || item.job_description || 'Job role details...',
        };
      case 'partners':
        return { name: item.name };
      case 'whyChoose':
      case 'why-choose':
        return {
          title: item.title,
          description: item.description || '',
          icon: item.icon || '✨',
          order: Number(item.order) || 1,
        };
      case 'whyJoin':
      case 'why-join':
        return {
          title: item.title,
          description: item.description || '',
          icon: item.icon || '✨',
          order: Number(item.order) || 1,
        };
      case 'offices':
        return {
          title: item.title,
          subtitle: item.subtitle || '',
          address: item.address,
          phone: item.phone,
          email: item.email,
          hours: item.hours,
          icon_type: item.icon_type || 'Building',
          order: Number(item.order) || 1,
        };
      default:
        return item;
    }
  };

  const mapItemFromBE = (section, be) => {
    if (!be) return {};

    switch (section) {
      case 'services':
        return {
          id: be.id,
          title: be.title || '',
          description: be.description || '',
          icon: be.icon || '🎓',
          color: be.color || '#c9a227',
        };
      case 'products':
        return {
          id: be.id,
          name: be.name || '',
          category: be.category_display || be.category || 'Other',
          price: be.price ? `₹${be.price}` : '₹0',
          description: be.description || '',
          icon: be.icon || '📦',
        };
      case 'gallery':
        return {
          id: be.id,
          title: be.title || '',
          category: be.category_display || be.category || 'Other',
          emoji: be.image || '🖼️',
          date: be.date || '',
        };
      case 'portfolio':
        return {
          id: be.id,
          title: be.project_title || be.title || '',
          client: be.client_name || '',
          category: be.category_display || be.category || 'Other',
          year: be.year ? String(be.year) : '2024',
          emoji: be.icon || '💼',
          description: be.description || '',
        };
      case 'team':
        return {
          id: be.id,
          name: be.full_name || '',
          role: be.role || '',
          department: be.department_display || be.department || 'Leadership',
          emoji: be.avatar_icon || '👨‍💼',
          email: be.email || '',
          experience: be.experience || '',
        };
      case 'testimonials':
        return {
          id: be.id,
          name: be.client_name || '',
          role: be.role_company || '',
          text: be.testimonial_text || '',
          rating: be.rating || 5,
        };
      case 'careers':
        return {
          id: be.id,
          title: be.job_title || '',
          department: be.department_display || be.department || 'Technology',
          location: be.location || '',
          type: be.job_type_display || be.job_type || 'Full Time',
          experience: be.experience_required || '',
          description: be.job_description || '',
        };
      case 'partners':
        return { id: be.id, name: be.name || '' };

      case 'journey':
        return {
          id: be.id,
          year: be.year || '',
          title: be.title || '',
          short_desc: be.short_desc || '',
          full_desc: be.full_desc || '',
          icon: be.icon || '🏆',
          sort_order: be.sort_order || 0,
        };
      default:
        return be;
    }
  };

  const getEndpoint = (section) => {
    if (section === 'whyChoose') return 'why-choose';
    if (section === 'whyJoin') return 'why-join';
    return section;
  };

  // ========================================================
  // 🌐 FETCH ALL DATA FROM BACKEND
  // ========================================================
  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [
        homeRes, aboutRes, contactRes,
        servicesRes, productsRes, galleryRes,
        portfolioRes, teamRes, testimonialsRes,
        careersRes, partnersRes, whyChooseRes, whyJoinRes,
        officesRes, inquiriesRes,journeyRes 
      ] = await Promise.all([
        api.get('/home-page/').catch(() => ({ data: null })),
        api.get('/about-us/').catch(() => ({ data: null })),
        api.get('/contact-info/').catch(() => ({ data: null })),
        api.get('/services/').catch(() => ({ data: [] })),
        api.get('/products/').catch(() => ({ data: [] })),
        api.get('/gallery/').catch(() => ({ data: [] })),
        api.get('/portfolio/').catch(() => ({ data: [] })),
        api.get('/team/').catch(() => ({ data: [] })),
        api.get('/testimonials/').catch(() => ({ data: [] })),
        api.get('/careers/').catch(() => ({ data: [] })),
        api.get('/partners/').catch(() => ({ data: [] })),
        api.get('/why-choose/').catch(() => ({ data: [] })),
        api.get('/why-join/').catch(() => ({ data: [] })),
        api.get('/offices/').catch(() => ({ data: [] })),
        api.get('/inquiries/').catch(() => ({ data: { results: [] } })),
        api.get('/journey/').catch(() => ({ data: [] })),
        
      ]);

      const inqData = inquiriesRes.data?.results || inquiriesRes.data || [];

      setData({
        home: mapHomeFromBE(homeRes.data),
        about: mapAboutFromBE(aboutRes.data),
        contact: mapContactFromBE(contactRes.data),
        services: (servicesRes.data || []).map((item) => mapItemFromBE('services', item)),
        products: (productsRes.data || []).map((item) => mapItemFromBE('products', item)),
        gallery: (galleryRes.data || []).map((item) => mapItemFromBE('gallery', item)),
        portfolio: (portfolioRes.data || []).map((item) => mapItemFromBE('portfolio', item)),
        team: (teamRes.data || []).map((item) => mapItemFromBE('team', item)),
        testimonials: (testimonialsRes.data || []).map((item) => mapItemFromBE('testimonials', item)),
        careers: (careersRes.data || []).map((item) => mapItemFromBE('careers', item)),
        partners: (partnersRes.data || []).map((item) => mapItemFromBE('partners', item)),
        whyChoose: (whyChooseRes.data || []).map((item) => mapItemFromBE('why-choose', item)),
        whyJoin: (whyJoinRes.data || []).map((item) => mapItemFromBE('why-join', item)),
        offices: (officesRes.data || []).map((item) => mapItemFromBE('offices', item)),
        inquiries: (Array.isArray(inqData) ? inqData : []).map((item) => mapItemFromBE('inquiries', item)),
        journey: (journeyRes.data || []).map((item) => mapItemFromBE('journey', item)),
      });

      setLoading(false);
    } catch (err) {
      console.error('Error fetching data from API:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  // ========================================================
  // 🔐 LOGIN & LOGOUT
  // ========================================================
  const login = async (usernameOrEmail, password) => {
    try {
      const response = await api.post('/auth/login/', {
        email: usernameOrEmail,
        password: password,
        com_name: 'holding_cmp'
      });

      const { access, refresh } = response.data;
      localStorage.setItem('access_token', access);
      if (refresh) localStorage.setItem('refresh_token', refresh);
      localStorage.setItem('sarwaAdminAuth', 'true');

      setIsAuthenticated(true);
      await fetchAllData();
      return true;
    } catch (error) {
      console.error('Login failed:', error.response?.data || error.message);
      alert(error.response?.data?.detail || "Login failed");
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('sarwaAdminAuth');
    setIsAuthenticated(false);
    setData(initialData);
  };

  // ========================================================
  // ✏️ UPDATE SINGLE SECTIONS (home, about, contact)
  // ========================================================
  const updateSection = async (section, newData) => {
    try {
      const updatedLocal = { ...data[section], ...newData };
      setData((prev) => ({ ...prev, [section]: updatedLocal }));

      let payload;
      let headers = {};

      if (section === 'home') {
        payload = new FormData();
        payload.append('hero_title', updatedLocal.heroTitle);
        payload.append('hero_description', updatedLocal.heroDesc);
        payload.append('tagline', updatedLocal.tagline);
        payload.append('year_established', parseInt(updatedLocal.yearEstablished) || 2021);
        if (newData.imageFile) payload.append('hero_image', newData.imageFile);
        headers = { 'Content-Type': 'multipart/form-data' };

        await api.put('/home-page/', payload, { headers });

      } else if (section === 'about') {
        payload = new FormData();
        payload.append('page_title', updatedLocal.title);
        payload.append('description', updatedLocal.description);
        payload.append('vision', updatedLocal.vision);
        payload.append('mission', updatedLocal.mission);
        payload.append('owner_name', updatedLocal.ownerName || '');
        if (newData.imageFile) payload.append('about_image', newData.imageFile);
        headers = { 'Content-Type': 'multipart/form-data' };

        await api.put('/about-us/', payload, { headers });

      } else if (section === 'contact') {
        await api.put('/contact-info/', mapContactToBE(updatedLocal));
      }

      fetchAllData();
    } catch (error) {
      console.error(`Error updating ${section}:`, error);
    }
  };

  // ========================================================
  // ➕ ADD ITEM TO ARRAY SECTION (DYNAMIC MULTIPART OPTIONAL FOR NEW ADDITIONS)
  // ========================================================
  const addItem = async (section, item) => {
    try {
      let payload;
      let headers = {};
      const endpoint = getEndpoint(section);

      // 🎯 For new gallery items, image is now optional!
      // We always use FormData to keep backend parser classes happy.
      if (['gallery', 'team', 'portfolio'].includes(section)) {
        payload = new FormData();
        headers = { 'Content-Type': 'multipart/form-data' };

        if (section === 'team') {
          payload.append('full_name', item.name || item.full_name);
          payload.append('role', item.role);
          payload.append('department', (item.department || 'leadership').toLowerCase());
          payload.append('email', item.email || '');
          payload.append('experience', item.experience || '');
          payload.append('education', item.education || '');
          payload.append('location', item.location || '');
          if (item.imageFile) payload.append('avatar_icon', item.imageFile);
        } else if (section === 'portfolio') {
          payload.append('project_title', item.title || item.project_title);
          payload.append('client_name', item.client || item.client_name);
          payload.append('category', (item.category || 'medical').toLowerCase());
          payload.append('year', parseInt(item.year) || 2024);
          payload.append('description', item.description || '');
          if (item.imageFile) payload.append('icon', item.imageFile);
        } else if (section === 'gallery') {
          payload.append('title', item.title);
          payload.append('category', item.category || 'Office');
          payload.append('date', item.date);
          if (item.imageFile) payload.append('image', item.imageFile); // 👈 Optional image append!
        }
      } else {
        payload = mapItemToBE(section, item);
      }

      const res = await api.post(`/${endpoint}/`, payload, { headers });
      const newFeItem = mapItemFromBE(section, res.data);

      setData((prev) => ({
        ...prev,
        [section]: [...(prev[section] || []), newFeItem],
      }));
    } catch (error) {
      console.error(`Error adding item to ${section}:`, error.response?.data || error);
    }
  };

  // ========================================================
  // 📝 UPDATE ITEM IN ARRAY SECTION (Verfied Regression-Free)
  // ========================================================
  const updateItem = async (section, id, updatedItem) => {
    try {
      let payload;
      let headers = {};
      const endpoint = getEndpoint(section);

      const isGalleryImageCleared = section === 'gallery' && updatedItem.imagePreview === '';

      if (
        (['gallery', 'team', 'portfolio'].includes(section) && updatedItem.imageFile) ||
        isGalleryImageCleared
      ) {
        payload = new FormData();
        headers = { 'Content-Type': 'multipart/form-data' };

        if (section === 'team') {
          payload.append('full_name', updatedItem.name || updatedItem.full_name);
          payload.append('role', updatedItem.role);
          payload.append('department', (updatedItem.department || 'leadership').toLowerCase());
          payload.append('email', updatedItem.email || '');
          payload.append('experience', updatedItem.experience || '');
          payload.append('education', updatedItem.education || '');
          payload.append('location', updatedItem.location || '');
          payload.append('avatar_icon', updatedItem.imageFile);
        } else if (section === 'portfolio') {
          payload.append('project_title', updatedItem.title || updatedItem.project_title);
          payload.append('client_name', updatedItem.client || updatedItem.client_name);
          payload.append('category', (updatedItem.category || 'medical').toLowerCase());
          payload.append('year', parseInt(updatedItem.year) || 2024);
          payload.append('description', updatedItem.description || '');
          payload.append('icon', updatedItem.imageFile);
        } else if (section === 'gallery') {
          payload.append('title', updatedItem.title);
          payload.append('category', updatedItem.category || 'Office');
          payload.append('date', updatedItem.date);

          if (updatedItem.imageFile) {
            payload.append('image', updatedItem.imageFile);
          } else if (isGalleryImageCleared) {
            payload.append('image', '');
          }
        }
      } else {
        const currentItem = data[section]?.find((i) => i.id === id) || {};
        const mergedItem = { ...currentItem, ...updatedItem };

        if (['gallery', 'team', 'portfolio'].includes(section)) {
          delete mergedItem.image;
          delete mergedItem.emoji;
        }

        payload = mapItemToBE(section, mergedItem);
      }

      const res = await api.put(`/${endpoint}/${id}/`, payload, { headers });
      const updatedFeItem = mapItemFromBE(section, res.data);

      setData((prev) => ({
        ...prev,
        [section]: prev[section].map((item) =>
          item.id === id ? updatedFeItem : item
        ),
      }));
    } catch (error) {
      console.error(`Error updating item in ${section}:`, error.response?.data || error);
    }
  };

  // ========================================================
  // 🗑️ DELETE ITEM FROM ARRAY SECTION
  // ========================================================
  const deleteItem = async (section, id) => {
    try {
      const endpoint = getEndpoint(section);

      setData((prev) => ({
        ...prev,
        [section]: prev[section].filter((item) => item.id !== id),
      }));

      await api.delete(`/${endpoint}/${id}/`);
    } catch (error) {
      console.error(`Error deleting item from ${section}:`, error.response?.data || error);
      fetchAllData();
    }
  };

  const resetData = () => {
    setData(initialData);
  };

  return (
    <AdminContext.Provider
      value={{
        data,
        isAuthenticated,
        loading,
        login,
        logout,
        updateSection,
        addItem,
        updateItem,
        deleteItem,
        resetData,
        fetchAllData,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};